import { css, styled } from "styled-components";

import { AppStoreiOSIcon, Link, LinkedInIcon, ResponsiveImage, XIcon } from "common/components";
import { breakpoints } from "common/styles";
import { useTranslate } from "common/utilities/useTranslate";
import { GooglePlayStoreLogoImageSet, HuaweiStoreLogoImageSet, SIAppLogoImageSet } from "domains/about/images/footer";
import { Section } from "domains/about/sections/Section";
import { SectionPreTitle } from "domains/about/sections/SectionPreTitle";
import { SectionText } from "domains/about/sections/SectionText";
import { SectionTitle } from "domains/about/sections/SectionTitle";
import { SectionTitleBlock } from "domains/about/sections/SectionTitleBlock";
import { interpolate } from "domains/language/interpolate";

const StyledSection = styled(Section)`
  text-align: center;
  align-items: center;
  justify-content: center;
`;

const StyledSectionText = styled(SectionText)`
  color: ${({ theme }) => theme.colors.textFade};
  margin-bottom: 3rem;
`;

const StyledSectionTitleBlock = styled(SectionTitleBlock)`
  margin-bottom: 1rem;
`;

const StyledContent = styled.div`
  width: 100%;
  max-width: 600px;
`;

const SIAppLogoImage = styled(ResponsiveImage)`
  width: 10rem;
  border-radius: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 0 0.5rem 0.1rem #3b4353;
`;

const StyledLogos = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;

  @media ${breakpoints.medium} {
    flex-direction: row;
  }
`;

const StyledLogo = styled.a`
  width: 60%;
  width: auto;

  &:not(:last-child) {
    margin-bottom: 1rem;
  }

  @media ${breakpoints.medium} {
    &:not(:last-child) {
      margin-right: 2rem;
      margin-bottom: 0;
    }
  }
`;

const GooglePlayStoreLogoImage = styled(ResponsiveImage)`
  width: auto;
  height: 3rem;
`;

const StyledSocialLogos = styled(StyledLogos)`
  gap: 25px;
`;

const StyledSocialLink = styled(Link)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
`;

const StyledSocialText = styled(SectionText)`
  margin-bottom: 0;
`;

const iconStyle = css`
  color: ${({ theme }) => theme.colors.textBright};
  width: 27px;
  margin-left: 0.75rem;
  margin-right: 0.75rem;
`;

const StyledLink = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.blue};
  &:hover {
    color: ${({ theme }) => theme.colors.blueDark};
  }
`;

const StyledXIcon = styled(XIcon)`
  ${iconStyle};
`;

const StyledLinkedInIcon = styled(LinkedInIcon)`
  ${iconStyle};
`;

const StyledAppStoreIcon = styled(AppStoreiOSIcon)`
  height: 3rem;
`;

type FooterProps = { className?: string };

export const Footer = ({ className }: FooterProps) => {
  const translate = useTranslate();

  return (
    <StyledSection className={className}>
      <StyledContent>
        <SIAppLogoImage srcSet={SIAppLogoImageSet} alt="App store" />
        <SectionPreTitle>{translate("app.about.footer.title")}</SectionPreTitle>
        <StyledSectionTitleBlock>
          <SectionTitle style={{ fontSize: "2rem" }}>{translate("app.about.footer.sub_title")}</SectionTitle>
          <StyledSectionText>{translate("app.about.footer.text")}</StyledSectionText>
        </StyledSectionTitleBlock>
        <StyledLogos>
          <StyledLogo href="https://apps.apple.com/gb/app/strategic-iq/id1454211238" target="_blank">
            <StyledAppStoreIcon />
          </StyledLogo>
          <StyledLogo
            href="https://play.google.com/store/apps/details?id=org.weforum.intelligence&hl=en_GB"
            target="_blank"
          >
            <GooglePlayStoreLogoImage srcSet={GooglePlayStoreLogoImageSet} alt="Google Play Store" />
          </StyledLogo>
          <StyledLogo href="https://appgallery.cloud.huawei.com/marketshare/app/C100941449" target="_blank">
            <ResponsiveImage srcSet={HuaweiStoreLogoImageSet} alt="Huawei store" />
          </StyledLogo>
        </StyledLogos>
        <SectionText>
          {interpolate(translate("app.about.footer.support_text"), {
            emailLink: (emailLink: string) => (
              <StyledLink href="mailto:intelligence@weforum.org" title={emailLink}>
                {emailLink}
              </StyledLink>
            ),
          })}
        </SectionText>
        <StyledSocialLogos>
          <StyledSocialLink href="https://x.com/WEF_Intel" target="_blank" rel="noopener">
            <StyledXIcon />
            <StyledSocialText>X</StyledSocialText>
          </StyledSocialLink>
          <StyledSocialLink
            href="https://www.linkedin.com/company/world-economic-forum/"
            target="_blank"
            rel="noopener"
          >
            <StyledLinkedInIcon />
            <StyledSocialText>LinkedIn</StyledSocialText>
          </StyledSocialLink>
        </StyledSocialLogos>
      </StyledContent>
    </StyledSection>
  );
};
